import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { axiosInstance } from '../config/axios';
import { toast } from 'sonner';
import CardCapsule from '../components/ui/CardCapsule';
import ServiceCard from '../components/services/ServiceCard';

const PRODUCT_CATEGORIES = ['All', 'Apparel', 'Accessories', 'Equipment', 'Limited'];
const SERVICE_CATEGORIES = [
  'All',
  'Body Massages',
  'Body Scrubs',
  'Body Masques',
  'Face Refreshers',
  'Foot Therapies',
  'Head Massages',
];

export default function ShopPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const initialTab = searchParams.get('tab') === 'services' ? 'services' : 'products';
  const [tab, setTab] = useState(initialTab);

  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [activePartnerId, setActivePartnerId] = useState('All');
  const [cartCount, setCartCount] = useState(0);
  const [collapsed, setCollapsed] = useState(false);
  const headerEndRef = useRef(null);
  const searchInputRef = useRef(null);

  // Items in the cart, for the badge. Refreshed each time the page opens
  // (adding to the cart happens on the product page).
  useEffect(() => {
    axiosInstance.get('/cart')
      .then((r) => setCartCount((r.data?.items || []).reduce((n, it) => n + (it.quantity || 1), 0)))
      .catch(() => {});
  }, []);

  // Once the full header has scrolled away, show the compact bar instead so
  // the cards get the screen.
  useEffect(() => {
    const el = headerEndRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(([entry]) => setCollapsed(!entry.isIntersecting && entry.boundingClientRect.top < 0));
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // The compact bar's search button brings the full header (and its search
  // box) back.
  const openSearch = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    setTimeout(() => searchInputRef.current?.focus({ preventScroll: true }), 350);
  };

  // Reset filters when toggling tabs
  useEffect(() => {
    setActiveCategory('All');
    setSearchQuery('');
    setActivePartnerId('All');
    setSearchParams({ tab }, { replace: true });
  }, [tab]);  // eslint-disable-line

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [p, s, pr] = await Promise.all([
          axiosInstance.get('/products'),
          axiosInstance.get('/services'),
          axiosInstance.get('/partners'),
        ]);
        setProducts(p.data);
        setServices(s.data);
        setPartners(pr.data);
      } catch (e) {
        toast.error('Failed to load shop');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const formatCurrency = (amount) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);

  const normalizedQuery = searchQuery.trim().toLowerCase();

  const filteredProducts = useMemo(
    () =>
      products
        .filter((p) => (activeCategory === 'All' ? true : p.category === activeCategory))
        .filter((p) => {
          if (!normalizedQuery) return true;
          return `${p.name} ${p.brand}`.toLowerCase().includes(normalizedQuery);
        }),
    [products, activeCategory, normalizedQuery],
  );

  const filteredServices = useMemo(
    () =>
      services
        .filter((s) =>
          activeCategory === 'All' ? true : s.service_category === activeCategory,
        )
        .filter((s) => (activePartnerId === 'All' ? true : s.partner_id === activePartnerId))
        .filter((s) => {
          if (!normalizedQuery) return true;
          return `${s.name} ${s.partner_brand || ''}`.toLowerCase().includes(normalizedQuery);
        }),
    [services, activeCategory, normalizedQuery, activePartnerId],
  );

  const categories = tab === 'services' ? SERVICE_CATEGORIES : PRODUCT_CATEGORIES;

  const cartButton = (size) => (
    <button
      onClick={() => navigate('/cart')}
      className={`relative ${size} bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm flex-shrink-0`}
      aria-label={cartCount ? `Cart, ${cartCount} items` : 'Cart'}
      data-testid={size === 'w-12 h-12' ? 'cart-button' : 'cart-button-compact'}
    >
      <i className="ph-bold ph-shopping-cart text-xl text-gray-700" />
      {cartCount > 0 && (
        <span
          className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-[#E54562] text-white text-[10px] font-bold flex items-center justify-center ring-2 ring-[#F9FAFB]"
          data-testid="cart-badge"
        >
          {cartCount > 99 ? '99+' : cartCount}
        </span>
      )}
    </button>
  );

  const categoryChips = (testId) => (
    <div className="flex gap-2 overflow-x-auto no-scrollbar" data-testid={testId}>
      {categories.map((cat) => (
        <button
          key={cat}
          onClick={() => setActiveCategory(cat)}
          className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap ${
            activeCategory === cat
              ? 'bg-[#2B3A67] text-white'
              : 'bg-white border border-gray-100'
          }`}
          data-testid={`${testId === 'shop-category-tabs' ? 'shop' : 'shop-compact'}-category-${cat.toLowerCase().replace(/ /g, '-')}`}
        >
          {cat}
        </button>
      ))}
    </div>
  );

  const activePartnerName = partners.find((p) => p.id === activePartnerId)?.name;

  return (
    <div
      className="min-h-screen bg-[#F9FAFB] pb-nav text-[#2B3A67]"
      style={{ fontFamily: "'Outfit', sans-serif" }}
    >
      {/* Compact bar — shown once the full header below has scrolled away */}
      <div
        className={`fixed top-0 left-0 right-0 z-30 bg-[#F9FAFB]/95 backdrop-blur-md border-b border-gray-100 shadow-sm pt-safe transition-all duration-200 ${
          collapsed ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-full pointer-events-none'
        }`}
        aria-hidden={!collapsed}
        data-testid="shop-compact-header"
      >
        <div className="flex items-center gap-2 px-4 pt-2.5 pb-2">
          <p className="text-lg font-extrabold">Market</p>
          <div className="flex bg-[#EEF0F4] rounded-full p-0.5 ml-1" role="tablist" aria-label="Shop or services">
            {[['products', 'Shop'], ['services', 'Services']].map(([id, label]) => (
              <button
                key={id}
                role="tab"
                aria-selected={tab === id}
                onClick={() => setTab(id)}
                className={`px-3 py-1 rounded-full text-xs font-bold transition-colors ${
                  tab === id ? 'bg-[#2B3A67] text-white' : 'text-gray-500'
                }`}
                data-testid={`shop-compact-tab-${id}`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={openSearch}
              className="w-9 h-9 bg-white rounded-full flex items-center justify-center border border-gray-100 shadow-sm"
              aria-label="Search"
              data-testid="shop-compact-search"
            >
              <i className="ph-bold ph-magnifying-glass text-lg text-gray-700" />
            </button>
            {cartButton('w-9 h-9')}
          </div>
        </div>
        <div className="flex gap-2 items-center px-4 pb-2.5">
          {tab === 'services' && partners.length > 0 && (
            // Native picker keeps this to one chip (and gives iOS its wheel).
            <label
              className="relative flex-shrink-0 flex items-center gap-1 px-3 py-2 rounded-full bg-[#E54562] text-white text-xs font-bold uppercase tracking-wider whitespace-nowrap"
              data-testid="shop-compact-partner"
            >
              {activePartnerName || 'All partners'}
              <i className="ph-bold ph-caret-down text-[10px]" />
              <select
                value={activePartnerId}
                onChange={(e) => setActivePartnerId(e.target.value)}
                className="absolute inset-0 opacity-0 w-full"
                aria-label="Partner"
              >
                <option value="All">All partners</option>
                {partners.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </label>
          )}
          <div className="min-w-0 flex-1">{categoryChips('shop-compact-category-tabs')}</div>
        </div>
      </div>

      <header className="bg-[#F9FAFB] border-b border-gray-100 pt-safe">
        <div className="flex items-center justify-between px-6 py-5">
          <div>
            <p
              className="text-xs tracking-[0.35em] uppercase text-gray-400 font-semibold"
              data-testid="shop-eyebrow"
            >
              Backyard
            </p>
            <h1 className="text-3xl font-extrabold text-[#2B3A67]" data-testid="shop-title">
              Market
            </h1>
            <p className="text-sm text-gray-500" data-testid="shop-subtitle">
              {tab === 'services'
                ? 'Curated wellness from our partners.'
                : 'Curated gear for the community.'}
            </p>
          </div>
          {cartButton('w-12 h-12')}
        </div>

        {/* Tab toggle */}
        <div className="px-6 pb-3" data-testid="shop-tab-toggle">
          <div className="bg-white rounded-2xl border border-gray-100 p-1 flex">
            <button
              data-testid="shop-tab-products"
              onClick={() => setTab('products')}
              className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-colors ${
                tab === 'products' ? 'bg-[#2B3A67] text-white' : 'text-gray-500'
              }`}
            >
              Shop
            </button>
            <button
              data-testid="shop-tab-services"
              onClick={() => setTab('services')}
              className={`flex-1 py-2.5 rounded-xl text-xs font-extrabold uppercase tracking-wider transition-colors ${
                tab === 'services' ? 'bg-[#2B3A67] text-white' : 'text-gray-500'
              }`}
            >
              Services
            </button>
          </div>
        </div>

        <div className="px-6 pb-4">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 px-4 py-3 mb-4 flex items-center gap-3">
            <i className="ph-bold ph-magnifying-glass text-lg text-gray-400" />
            <input
              ref={searchInputRef}
              data-testid="shop-search-input"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                tab === 'services' ? 'Search services or partners' : 'Search products or brands'
              }
              className="flex-1 text-sm focus:outline-none text-[#2B3A67]"
            />
            {searchQuery && (
              <button
                data-testid="shop-search-clear"
                onClick={() => setSearchQuery('')}
                className="text-xs font-bold text-[#E54562]"
              >
                Clear
              </button>
            )}
          </div>

          {categoryChips('shop-category-tabs')}
          {/* Partner pill row — services only */}
          {tab === 'services' && partners.length > 0 && (
            <div
              className="flex gap-2 overflow-x-auto no-scrollbar mt-3"
              data-testid="shop-partner-tabs"
            >
              <button
                onClick={() => setActivePartnerId('All')}
                className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap ${
                  activePartnerId === 'All'
                    ? 'bg-[#E54562] text-white'
                    : 'bg-white border border-gray-100 text-gray-500'
                }`}
                data-testid="shop-partner-all"
              >
                All Partners
              </button>
              {partners.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActivePartnerId(p.id)}
                  className={`px-3 py-1.5 rounded-full text-[10px] font-extrabold uppercase tracking-wider whitespace-nowrap ${
                    activePartnerId === p.id
                      ? 'bg-[#E54562] text-white'
                      : 'bg-white border border-gray-100 text-gray-500'
                  }`}
                  data-testid={`shop-partner-${p.slug}`}
                >
                  {p.name}
                </button>
              ))}
            </div>
          )}
        </div>
        <div ref={headerEndRef} aria-hidden="true" />
      </header>

      <main className="px-6 pt-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#E54562] mx-auto" />
            <p data-testid="shop-loading" className="mt-4 text-gray-400">
              Loading...
            </p>
          </div>
        ) : tab === 'services' ? (
          filteredServices.length === 0 ? (
            <div className="text-center py-12">
              <p data-testid="shop-empty" className="text-gray-400">
                No services available for this filter.
              </p>
            </div>
          ) : (
            <div
              className="grid grid-cols-2 md:grid-cols-3 gap-4"
              data-testid="shop-services-grid"
            >
              {filteredServices.map((s, idx) => (
                <ServiceCard key={s.id} service={s} index={idx} />
              ))}
            </div>
          )
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-12">
            <p data-testid="shop-empty" className="text-gray-400">
              No products available right now.
            </p>
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-3 gap-4"
            data-testid="shop-products-grid"
          >
            {filteredProducts.map((product, index) => (
              <div
                key={product.id}
                data-testid={`product-card-${index}`}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/shop/${product.id}`)}
                onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/shop/${product.id}`); }}
                className="bg-white rounded-2xl overflow-hidden shadow-md border border-gray-100 cursor-pointer transition-transform active:scale-[0.98]"
              >
                <div className="relative">
                  <img
                    data-testid={`product-image-${product.id}`}
                    src={product.images[0]}
                    alt={product.name}
                    className="w-full h-36 object-cover"
                  />
                  {product.brand && (
                    <span
                      data-testid={`product-brand-${product.id}`}
                      className="absolute top-2 left-2 max-w-[60%] truncate bg-[#2B3A67] text-white text-[10px] font-black px-2 py-1 rounded-full uppercase tracking-wider"
                    >
                      {product.brand}
                    </span>
                  )}
                  {product.discounted_price && (
                    <span
                      data-testid={`product-sale-${product.id}`}
                      className="absolute top-2 right-2 bg-[#F4D341] text-[#2B3A67] text-[10px] font-black px-2 py-1 rounded-full"
                    >
                      SALE
                    </span>
                  )}
                  <CardCapsule label="Buy" testId={`product-add-${product.id}`} />
                </div>

                <div className="p-4">
                  <h3
                    data-testid={`product-name-${product.id}`}
                    className="font-bold text-sm leading-snug line-clamp-2"
                  >
                    {product.name}
                  </h3>
                  {product.category && (
                    <p className="text-xs text-gray-500 mt-0.5 truncate">{product.category}</p>
                  )}
                  <p className="mt-2 leading-none">
                    <span
                      data-testid={`product-price-${product.id}`}
                      className="font-black text-[#2B3A67]"
                    >
                      {formatCurrency(product.discounted_price || product.base_price)}
                    </span>
                    {product.discounted_price && (
                      <span
                        data-testid={`product-base-price-${product.id}`}
                        className="ml-1.5 text-xs text-gray-400 line-through"
                      >
                        {formatCurrency(product.base_price)}
                      </span>
                    )}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
